/**
 * taskConstants - the ONE list of task statuses and priorities.
 *
 * Three files disagreed about these. `Tasks.tsx` offered low/medium/high/
 * critical; `TaskForm` and `AddTaskComponent` each had their own copy with
 * their own labels and their own colour mapping, so the same task could show
 * as "Critical" in one place and "critical" in another, in a different
 * colour, and a status set in one editor could be unrepresentable in the
 * next. These are the values the API accepts; everything renders from here.
 *
 * Labels are untranslated - callers run them through `t`.
 */

import type { BadgeColor } from '../../shared/TailwindComponents/types/badgeTypes';

export type TaskPriority = 'low' | 'medium' | 'high' | 'critical';
export type TaskStatus = 'todo' | 'in_progress' | 'completed' | 'archived';

export interface TaskOption<T extends string> {
  value: T;
  label: string;
}

export const TASK_PRIORITIES: TaskOption<TaskPriority>[] = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

/**
 * The statuses a person can SET. `archived` is deliberately absent: it is
 * reached through the Archive action, not by picking it from a dropdown, and
 * offering it here would give two paths to the same state that behave
 * differently (one calls `archiveTask`, one would call `updateTask`).
 */
export const TASK_STATUSES: TaskOption<TaskStatus>[] = [
  { value: 'todo', label: 'To do' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'completed', label: 'Completed' },
];

/**
 * Fully spelled-out classes - Tailwind only compiles class names it can see in
 * the source, so a runtime-composed `text-${tone}-500` never existed in the CSS
 * and the flag rendered near-black. Never build these by interpolation.
 */
const PRIORITY_FLAG_CLASS: Record<TaskPriority, string> = {
  low: 'text-success-500',
  medium: 'text-warning-500',
  high: 'text-warning-600',
  critical: 'text-danger-500',
};

const STATUS_BADGE: Record<string, BadgeColor> = {
  todo: 'gray',
  in_progress: 'blue',
  completed: 'green',
  archived: 'slate',
  deleted: 'slate',
};

/** Full text-colour class for a priority flag icon. */
export const priorityFlagClass = (priority?: string): string =>
  PRIORITY_FLAG_CLASS[(priority || '').toLowerCase() as TaskPriority] || 'text-gray-400';

export const statusBadgeColor = (status?: string): BadgeColor =>
  STATUS_BADGE[(status || '').toLowerCase()] || 'gray';

/** `in_progress` → `In progress`. Falls back to prettifying the raw value. */
export const taskStatusLabel = (status?: string): string => {
  const key = (status || 'todo').toLowerCase();
  // The API says `deleted` where the UI has always said Archived.
  if (key === 'deleted' || key === 'archived') return 'Archived';
  const known = TASK_STATUSES.find((s) => s.value === key);
  if (known) return known.label;
  return key.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
};

export const taskPriorityLabel = (priority?: string): string => {
  const key = (priority || '').toLowerCase();
  return TASK_PRIORITIES.find((p) => p.value === key)?.label
    || key.replace(/^\w/, (c) => c.toUpperCase())
    || 'None';
};

/** True for a task that is finished or filed away - the row dims either way. */
export const isTaskClosed = (status?: string): boolean =>
  ['completed', 'archived', 'deleted'].includes((status || '').toLowerCase());

/**
 * A task's STABLE client id.
 *
 * The uuid where there is one, else the numeric id. Never the array index —
 * that is what let a background refresh reorder the list and move an open
 * editor onto a different task.
 */
export const taskId = (task: any): string =>
  /*
    `||`, NOT `??`.
    
    Nullish coalescing keeps an EMPTY STRING, so a payload that returns
    `uuid: ""` gave every task the same id `""` - every row selected the same
    thing and the detail container stayed on whichever task was opened first.
    `||` falls through empty strings and zeros to the next real identifier,
    which is what the table this replaced always did.
  */
  String(task?.uuid || task?.TaskID || task?.id || task?.task_id || '');

/** A suggestion has no server id until it is accepted. */
export const suggestionId = (suggestion: any, index: number): string =>
  // Same reason as `taskId`: an empty id must fall through, not be kept.
  String(suggestion?.id || suggestion?.uuid || `${suggestion?.TaskTitle || 'suggestion'}#${index}`);
